import "../Styles/Work.css";
import { Link } from "react-router-dom";
import { MdWork, MdPublic, MdAttachMoney, MdTrendingUp, MdCardTravel, MdDescription, MdCheckCircle } from 'react-icons/md';
import { Helmet } from "react-helmet";
import { PageHero, CtaBand } from "./PageParts";
import Reveal, { RevealGroup, RevealItem } from "./Reveal";

export default function Work() {
  return (
    <div className="work-page-modern ve-scope">
      <Helmet>
        <title>Work Abroad Visa Services | Canada LMIA, UK, USA Work Permits</title>
        <meta name="description" content="Apply for Canada LMIA work permits, UK Skilled Worker, Australia TSS 482, and USA H-1B visas. Get expert resume building and job search support today!" />
        <link rel="canonical" href="https://veabroad.com/work" />
        <meta property="og:title" content="Work Abroad Visa Services | Canada LMIA, UK, USA Work Permits" />
        <meta property="og:description" content="Apply for Canada LMIA work permits, UK Skilled Worker, Australia TSS 482, and USA H-1B visas. Get expert resume building and job search support today!" />
        <meta property="og:url" content="https://veabroad.com/work" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://veabroad.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Work Abroad Visa Services | Canada LMIA, UK, USA Work Permits" />
        <meta name="twitter:description" content="Apply for Canada LMIA work permits, UK Skilled Worker, Australia TSS 482, and USA H-1B visas. Get expert resume building and job search support today!" />
        <meta name="twitter:image" content="https://veabroad.com/og-image.jpg" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Work Visa & Job Search Assistance",
            "provider": {
              "@type": "EducationalOrganization",
              "name": "Vidya Education Abroad",
              "url": "https://veabroad.com/"
            },
            "areaServed": "IN",
            "description": "Professional support for acquiring foreign work permits and building international career roadmaps."
          })}
        </script>
      </Helmet>

      {/* Cinematic Hero */}
      <PageHero
        image="/work-hero.webp"
        eyebrow="Work abroad"
        title={<>A career that <em>crosses borders</em>, filed properly.</>}
        lede="Job search support, a CV rebuilt to international format, employer sponsorship and job-seeker visas — handled end to end so the paperwork never costs you the offer."
        ctas={[
          { to: "/consultationForm", label: "Check your eligibility" },
          { href: "#destinations", label: "Explore destinations", variant: "ghost" },
        ]}
        stats={[
          { figure: "500+", label: "Work visas processed" },
          { figure: "92%", label: "Approval rate" },
        ]}
      />

      {/* Modern Features Grid */}
      <section className="py-6 section-features">
        <div className="container">
          <RevealGroup as="div" className="row g-4" stagger={0.12}>
            <RevealItem as="div" className="col-lg-3 col-md-6">
              <div className="feature-card-modern va-hover-lift p-4 h-100">
                <div className="feature-icon-wrapper mb-4">
                  <MdAttachMoney className="text-success" />
                </div>
                <h4>Higher Earnings</h4>
                <p className="text-muted">Access competitive salaries and better financial opportunities abroad.</p>
              </div>
            </RevealItem>
            <RevealItem as="div" className="col-lg-3 col-md-6">
              <div className="feature-card-modern va-hover-lift p-4 h-100">
                <div className="feature-icon-wrapper mb-4">
                  <MdPublic className="text-primary" />
                </div>
                <h4>Global Exposure</h4>
                <p className="text-muted">Work with top international companies and expand your professional network.</p>
              </div>
            </RevealItem>
            <RevealItem as="div" className="col-lg-3 col-md-6">
              <div className="feature-card-modern va-hover-lift p-4 h-100">
                <div className="feature-icon-wrapper mb-4">
                  <MdTrendingUp className="text-warning" />
                </div>
                <h4>Career Growth</h4>
                <p className="text-muted">Accelerate your career trajectory with international experience.</p>
              </div>
            </RevealItem>
            <RevealItem as="div" className="col-lg-3 col-md-6">
              <div className="feature-card-modern va-hover-lift p-4 h-100">
                <div className="feature-icon-wrapper mb-4">
                  <MdWork className="text-info" />
                </div>
                <h4>PR Pathways</h4>
                <p className="text-muted">Convert your work experience into permanent residency abroad.</p>
              </div>
            </RevealItem>
          </RevealGroup>
        </div>
      </section>

      {/* Reveal Cards Destinations */}
      <section id="destinations" className="py-6 bg-light-mesh position-relative">
        <div className="va-blob-field" aria-hidden="true">
          <div className="va-blob va-blob-mint" style={{ top: "10%", left: "88%", width: "260px", height: "260px" }}></div>
          <div className="va-blob va-blob-teal" style={{ top: "65%", left: "-5%", width: "240px", height: "240px" }}></div>
        </div>
        <div className="container position-relative">
          <Reveal as="h2" direction="up" className="text-center mb-5 display-5 fw-bold">
            Top Work Destinations
          </Reveal>
          <RevealGroup as="div" className="row g-4" stagger={0.08}>
            <RevealItem as="div" direction="zoom" className="col-lg-3 col-md-6">
              <div className="reveal-card va-hover-lift">
                <div className="reveal-bg" style={{backgroundImage: "url('https://images.unsplash.com/photo-1503614472-8c93d56e92ce?w=400&h=500&fit=crop&q=65')"}}></div>
                <div className="reveal-overlay"></div>
                <div className="reveal-content-front">
                  <div className="flag-icon mb-2">🇨🇦</div>
                  <h3>Canada</h3>
                </div>
                <div className="reveal-content-hover">
                  <h3>Canada</h3>
                  <p>LMIA-based work permits with direct PR pathways.</p>
                  <ul className="list-unstyled small mt-3">
                    <li><MdCheckCircle className="text-success me-2" />High demand sectors</li>
                    <li><MdCheckCircle className="text-success me-2" />Family sponsorship</li>
                    <li><MdCheckCircle className="text-success me-2" />PR in 1-2 years</li>
                  </ul>
                  <Link to="/consultationForm" className="modern-btn primary sm mt-3 w-100">Apply Now</Link>
                </div>
              </div>
            </RevealItem>

            <RevealItem as="div" direction="zoom" className="col-lg-3 col-md-6">
              <div className="reveal-card va-hover-lift">
                <div className="reveal-bg" style={{backgroundImage: "url('https://images.unsplash.com/photo-1523482580672-f109ba8cb9be?w=400&h=500&fit=crop&q=65')"}}></div>
                <div className="reveal-overlay"></div>
                <div className="reveal-content-front">
                  <div className="flag-icon mb-2">🇦🇺</div>
                  <h3>Australia</h3>
                </div>
                <div className="reveal-content-hover">
                  <h3>Australia</h3>
                  <p>Employer-sponsored visas (TSS 482) with PR options.</p>
                  <ul className="list-unstyled small mt-3">
                    <li><MdCheckCircle className="text-success me-2" />Skilled occupation list</li>
                    <li><MdCheckCircle className="text-success me-2" />High salaries</li>
                    <li><MdCheckCircle className="text-success me-2" />Quality lifestyle</li>
                  </ul>
                  <Link to="/consultationForm" className="modern-btn primary sm mt-3 w-100">Apply Now</Link>
                </div>
              </div>
            </RevealItem>

            <RevealItem as="div" direction="zoom" className="col-lg-3 col-md-6">
              <div className="reveal-card va-hover-lift">
                <div className="reveal-bg" style={{backgroundImage: "url('/uk-work.webp')"}}></div>
                <div className="reveal-overlay"></div>
                <div className="reveal-content-front">
                  <div className="flag-icon mb-2">🇬🇧</div>
                  <h3>United Kingdom</h3>
                </div>
                <div className="reveal-content-hover">
                  <h3>UK</h3>
                  <p>Skilled Worker visa with a 5-year settlement route.</p>
                  <ul className="list-unstyled small mt-3">
                    <li><MdCheckCircle className="text-success me-2" />No cap on visas</li>
                    <li><MdCheckCircle className="text-success me-2" />Family inclusion</li>
                    <li><MdCheckCircle className="text-success me-2" />ILR after 5 years</li>
                  </ul>
                  <Link to="/consultationForm" className="modern-btn primary sm mt-3 w-100">Apply Now</Link>
                </div>
              </div>
            </RevealItem>

            <RevealItem as="div" direction="zoom" className="col-lg-3 col-md-6">
              <div className="reveal-card va-hover-lift">
                <div className="reveal-bg" style={{backgroundImage: "url('https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?w=400&h=500&fit=crop&q=65')"}}></div>
                <div className="reveal-overlay"></div>
                <div className="reveal-content-front">
                  <div className="flag-icon mb-2">🇺🇸</div>
                  <h3>USA</h3>
                </div>
                <div className="reveal-content-hover">
                  <h3>USA</h3>
                  <p>H-1B specialty occupation visas and L1 transfers.</p>
                  <ul className="list-unstyled small mt-3">
                    <li><MdCheckCircle className="text-success me-2" />Tech opportunities</li>
                    <li><MdCheckCircle className="text-success me-2" />Top salaries globally</li>
                    <li><MdCheckCircle className="text-success me-2" />Green card pathway</li>
                  </ul>
                  <Link to="/consultationForm" className="modern-btn primary sm mt-3 w-100">Apply Now</Link>
                </div>
              </div>
            </RevealItem>
          </RevealGroup>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-6 section-services">
        <div className="container">
          <Reveal as="h2" direction="up" className="text-center mb-5 display-5 fw-bold">
            Comprehensive Support
          </Reveal>
          <RevealGroup as="div" className="row g-4" stagger={0.1}>
            <RevealItem as="div" className="col-md-4">
              <div className="service-card-modern va-hover-lift p-5 h-100">
                <div className="service-icon-wrapper">
                  <MdCardTravel className="service-icon" />
                </div>
                <h4 className="mb-3">Visa Assessment</h4>
                <p className="text-muted mb-0">We evaluate your eligibility for work visas across multiple countries based on your unique professional profile and goals.</p>
              </div>
            </RevealItem>
            <RevealItem as="div" className="col-md-4">
              <div className="service-card-modern va-hover-lift p-5 h-100">
                <div className="service-icon-wrapper">
                  <MdDescription className="service-icon" />
                </div>
                <h4 className="mb-3">Documentation</h4>
                <p className="text-muted mb-0">Complete assistance with resume structuring, cover letters, credential evaluation, and gathering all necessary visa documents.</p>
              </div>
            </RevealItem>
            <RevealItem as="div" className="col-md-4">
              <div className="service-card-modern va-hover-lift p-5 h-100">
                <div className="service-icon-wrapper">
                  <MdWork className="service-icon" />
                </div>
                <h4 className="mb-3">Job Search Strategy</h4>
                <p className="text-muted mb-0">We help you connect with global employers, job portals, and recruitment agencies in your target destination.</p>
              </div>
            </RevealItem>
          </RevealGroup>
        </div>
      </section>

      {/* Requirements & CTA Split */}
      <CtaBand
        title="Want to know which work visas you actually qualify for?"
        body="Send us your CV and we will tell you which countries, which visa routes and which employers are realistic for your experience — before you spend anything."
        primary={{ to: "/consultationForm", label: "Get a free profile review" }}
        secondary={{ to: "/visa", label: "See all visa services" }}
      />
    </div>
  );
}